import React, { useCallback } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@apollo/client/react';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { GetMealDocument, type Meal } from '~/graphql/generated/graphql';
import { StringUtils } from '~/global/utils/string.utils';
import {
  MealLogCard as MealLogCardView,
  type MealLogCardMeal,
} from '~/meal/components/meal-log-card/MealLogCard';
import { PresentationCaption } from './PresentationCaption';

interface MealLogRecordLike {
  id: string;
  mealId: string;
  consumedAmount: number | string | null;
  consumedAmountUnit: string | null;
}

interface MealLogCardProps {
  caption?: string | null;
  mealLogId: string;
  meal: Meal;
  consumedAmount: number;
  consumedAmountUnit?: string | null;
  onRemove: (mealLogId: string) => void;
  onAmountChange: (
    mealLogId: string,
    amount: number,
    unit: string | null,
  ) => void;
}

interface MealLogCardSyncedProps {
  payload: Record<string, unknown>;
}

interface MealLogPayload {
  caption?: string | null;
  mealLogId?: string;
}

/**
 * `present_meal_log` → an editable logged-meal card (portion input + unit
 * picker). Reuses the meal feature's `MealLogCard` view; edits mutate the real
 * meal log via the same mutations the Log Meal screen uses.
 */
export const MealLogCard: React.FC<MealLogCardProps> & {
  Synced: React.FC<MealLogCardSyncedProps>;
} = ({
  caption,
  mealLogId,
  meal,
  consumedAmount,
  onRemove,
  onAmountChange,
  consumedAmountUnit,
}) => (
  <View className="w-full py-2 gap-2">
    <PresentationCaption caption={caption} />
    <MealLogCardView
      mealLogId={mealLogId}
      meal={meal as MealLogCardMeal}
      consumedAmount={consumedAmount}
      consumedAmountUnit={consumedAmountUnit}
      onRemove={onRemove}
      onAmountChange={onAmountChange}
    />
  </View>
);

const MealLogCardSynced: React.FC<MealLogCardSyncedProps> = ({ payload }) => {
  const { t } = useTranslation('meal');
  const powerSync = usePowerSync();
  const { caption, mealLogId } = payload as MealLogPayload;

  const { data: mealLog, isLoading: isLoadingLog } =
    useLivePowerSyncQuery<MealLogRecordLike>(
      mealLogId ? db.MealLog.queries.findById(mealLogId) : null,
      [mealLogId],
      { single: true },
    );

  const { data: mealData, loading: isLoadingMeal } = useQuery(GetMealDocument, {
    variables: { id: mealLog?.mealId as string },
    skip: !mealLog?.mealId,
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      id: string;
      consumedAmount: number;
      consumedAmountUnit: string | null;
    }) =>
      db.MealLog.mutations.update(powerSync, input.id, {
        consumedAmount: input.consumedAmount,
        consumedAmountUnit: input.consumedAmountUnit,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => db.MealLog.mutations.delete(powerSync, id),
  });

  const handleRemove = useCallback(
    (id: string) => deleteMutation.mutate(id),
    [deleteMutation],
  );

  const handleAmountChange = useCallback(
    (id: string, amount: number, unit: string | null) =>
      updateMutation.mutate({
        id,
        consumedAmount: amount,
        consumedAmountUnit: unit,
      }),
    [updateMutation],
  );

  const meal = mealData?.meal as Meal | undefined;

  if (!mealLogId) {
    return null;
  }

  if (isLoadingLog || isLoadingMeal) {
    return (
      <View className="w-full py-2 gap-2">
        <PresentationCaption caption={caption} />
        <View className="rounded-2xl bg-surface p-5">
          <Text className="text-text-muted text-sm">{t('loading')}</Text>
        </View>
      </View>
    );
  }

  if (!mealLog || !meal) {
    return null;
  }

  const consumedAmount =
    StringUtils.parseFloat(mealLog.consumedAmount?.toString() ?? '1') ?? 1;

  return (
    <MealLogCard
      caption={caption}
      mealLogId={mealLog.id}
      meal={meal}
      consumedAmount={consumedAmount}
      consumedAmountUnit={mealLog.consumedAmountUnit}
      onRemove={handleRemove}
      onAmountChange={handleAmountChange}
    />
  );
};

MealLogCard.Synced = MealLogCardSynced;
